import Dagre from '@dagrejs/dagre';
import type { Edge, Node } from 'reactflow';
import type { NodeData } from './nodes/flowEditorBlockNodeTypes';
import { BLOCK_NODE_WIDTH } from '../constants';

export type LayoutDirection = 'LR' | 'TB';

export interface AutoLayoutOptions {
  direction?: LayoutDirection;
  nodeSeparation?: number;
  rankSeparation?: number;
}

// Dagre needs a width and height per node to reserve space. React Flow only
// knows a node's real size once it has rendered, and onlyRenderVisibleElements
// unmounts off-screen nodes (so their size is gone) - which is exactly the
// "tidy up a big flow" case. Without a fallback dagre reserves a zero-sized box
// and the ranks collapse into overlapping nodes, so we fall back to:
//   - width: the known fixed block width (every node is this wide).
//   - height: a deliberate over-estimate, since height genuinely varies with
//     content. Too tall just leaves harmless whitespace; too short overlaps.
const FALLBACK_NODE_HEIGHT = 120;

// Gaps dagre leaves between sibling nodes and between ranks. Tuned by eye for
// the conversation bubble size; expose them via options for callers that want
// tighter or looser layouts.
const DEFAULT_NODE_SEPARATION = 80;
const DEFAULT_RANK_SEPARATION = 120;

function nodeWidth(node: Node): number {
  return node.width ?? BLOCK_NODE_WIDTH;
}

function nodeHeight(node: Node): number {
  return node.height ?? FALLBACK_NODE_HEIGHT;
}

// The visual top-to-bottom order of a node's option pills, used to order edges
// going into dagre so branches come out in the same order the author sees them.
// Edges from a node's options carry the option id as their sourceHandle.
function optionIndex(
  node: Node<NodeData> | undefined,
  sourceHandle: string | null | undefined,
): number {
  const options = node?.data?.options;
  if (!options || sourceHandle == null) {
    return -1;
  }
  return options.findIndex((option) => option.id === sourceHandle);
}

/**
 * Arrange conversation flow nodes into a clean tree with dagre. Pure: it reads
 * the nodes/edges and returns new node positions, leaving edges untouched.
 *
 * Determinism matters: nodes and edges are fed to dagre in a stable order (edges
 * by their source's option index) so repeated runs produce the same layout and
 * sibling branches keep their option order, rather than the layout jumping each
 * time the button is pressed.
 */
export function getLayoutedElements(
  nodes: Node<NodeData>[],
  edges: Edge[],
  options: AutoLayoutOptions = {},
): { nodes: Node<NodeData>[]; edges: Edge[] } {
  const direction = options.direction ?? 'LR';

  if (nodes.length === 0) {
    return { nodes, edges };
  }

  const graph = new Dagre.graphlib.Graph().setDefaultEdgeLabel(() => ({}));
  graph.setGraph({
    rankdir: direction,
    ranker: 'network-simplex',
    nodesep: options.nodeSeparation ?? DEFAULT_NODE_SEPARATION,
    ranksep: options.rankSeparation ?? DEFAULT_RANK_SEPARATION,
    edgesep: 20, // px between parallel edges sharing a rank
    marginx: 40, // outer padding around the whole graph
    marginy: 40,
  });

  const nodeById = new Map(nodes.map((node) => [node.id, node]));

  for (const node of nodes) {
    graph.setNode(node.id, { width: nodeWidth(node), height: nodeHeight(node) });
  }

  const orderedEdges = [...edges].sort((a, b) => {
    // Only reorder edges that share a source (a node's own options); leave
    // everything else in input order. Relies on Array.sort being stable.
    if (a.source !== b.source) {
      return 0;
    }
    return (
      optionIndex(nodeById.get(a.source), a.sourceHandle) -
      optionIndex(nodeById.get(b.source), b.sourceHandle)
    );
  });

  for (const edge of orderedEdges) {
    // Skip edges whose endpoints are not in the set being laid out (e.g. a
    // selection-scoped layout); dagre throws on an edge to an unknown node.
    if (nodeById.has(edge.source) && nodeById.has(edge.target)) {
      graph.setEdge(edge.source, edge.target);
    }
  }

  Dagre.layout(graph);

  const layoutedNodes = nodes.map((node) => {
    const { x, y } = graph.node(node.id);
    // Dagre returns the node centre; React Flow positions by the top-left
    // corner, so shift by this node's own half-size (not a shared constant, or
    // variable-size nodes mis-centre).
    return {
      ...node,
      position: { x: x - nodeWidth(node) / 2, y: y - nodeHeight(node) / 2 },
    };
  });

  return { nodes: layoutedNodes, edges };
}
